import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { Popper, Box, Typography } from '@mui/material';
import { ALL_METRICS, averageSfrFed } from '../../constants/constantsAnalytic';

const BarChartSemanal = ({ chartData, alarmGraph, titulo, chartInstanceRef, translate }) => {
  const t = (key, fallback) => {
    const value = typeof translate === 'function' ? translate(key) : key;
    return value && value !== key ? value : fallback;
  };
  // ----- Refs e instancias -----
  const barChartRef = useRef(null);
  const barChartInstanceRef = useRef(null);
  const graceTimerRef = useRef(null);

  const borderWidth = 0.2;
  const colorLine = 'rgba(126, 121, 121, 0.38)';
  const fontSizeEjes = window.innerWidth * 0.0065;

  // ----- Tooltip (MUI Popper) -----
  const [tooltipState, setTooltipState] = useState({
    open: false,
    x: 0,
    y: 0,
    content: null,
  });
  // “lock” al entrar al tooltip
  const tooltipLockedRef = useRef(false);
  // timestamp de última visibilidad (gracia)
  const lastActiveTsRef = useRef(0);

  // ===== Helpers =====
  const formatTitleMaybeDate = (ts) => {
    if (ts == null) return '';
    // si ya es número (timestamp)
    const asNum = Number(ts);
    if (!Number.isNaN(asNum) && String(ts).trim() !== '') {
      const d = new Date(asNum);
      if (!isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
      }
    }
    // si es string ISO o no parseable, lo dejamos tal cual
    return String(ts);
  };
  // helper para cierre forzado (sin flicker)
  const forceClose = (chart) => {
    if (tooltipLockedRef.current) return;
    setTooltipState((prev) => ({ ...prev, open: false }));
    try {
      // Chart.js v3/v4: cualquiera de estas puede existir
      chart?.tooltip?.setActiveElements?.([], { x: 0, y: 0 });
      chart?.setActiveElements?.([]);
      chart?.update?.('none');
    } catch {}
  };

  // === Helper de orden natural para "Jaula 2" < "Jaula 10" ===
  const naturalCompare = (a, b) =>
    String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });

  // ----- Promedios por jaula -----
  const avgData = chartData.map((jaula) => {
    const summary = jaula.values_summary || [];
    let sumAlarmsByTon = 0,
      sumAlarmsCount = 0;
    summary.forEach((day) => {
      sumAlarmsByTon += day.value[alarmGraph?.[1]]; // alarms_by_ton o alarms_by_visits
      sumAlarmsCount += day.value.alarms_count;
    });
    return {
      name: jaula.name,
      // SFR promedio: solo días alimentados (excluye ayunos) y saneado (>1000% recalculado/descartado)
      avgSfr: Number(averageSfrFed(summary).toFixed(2)),
      avgAlarmsByTon: Number(sumAlarmsByTon.toFixed(2)), // acumulado
      avgAlarmsCount: Number(sumAlarmsCount.toFixed(2)), // acumulado
    };
  });

  // === ORDENAR JAULAS (ascendente). Cambia a 'desc' si lo necesitas ===
  const sortDir = 'asc'; // o 'desc'
  const sortedAvgData = [...avgData].sort((a, b) =>
    sortDir === 'asc' ? naturalCompare(a.name, b.name) : naturalCompare(b.name, a.name)
  );

  // Mapeo de keys
  const dataKeyMapping = {
    sfr_value: 'avgSfr',
    alarms_by_ton: 'avgAlarmsByTon',
    alarms_count: 'avgAlarmsCount',
  };
  const metricsToPlot = Object.keys(dataKeyMapping);

  // Construye datasets usando ALL_METRICS
  const barChartData = {
    // labels: avgData.map((item) => item.name),
    labels: sortedAvgData.map((item) => chartData?.find((j) => j.name === item.name)?.label ?? item.name),
    datasets: metricsToPlot.map((metricKey) => {
      const resolvedKey =
        metricKey === 'alarms_by_ton' ? alarmGraph?.[1] : metricKey; // puede ser alarms_by_visits
      const metric = ALL_METRICS.find((m) => m.key === resolvedKey) || {
        label: resolvedKey,
        color: '#888',
      };
      const metricLabel = resolvedKey === 'alarms_count'
        ? t('alarms', 'Alarmas')
        : resolvedKey === 'alarms_by_visits'
          ? t('alarms_per_visit', 'Alarmas por visita')
          : resolvedKey === 'alarms_by_ton'
            ? t('alarms_per_ton', 'Alarmas por tonelada')
            : resolvedKey === 'sfr_value'
              ? t('average_sfr', 'SFR promedio')
              : metric.label;
      const axisID =
        metricKey === 'sfr_value'
          ? 'y-left'
          : metricKey === 'alarms_by_ton' || metricKey === 'alarms_by_visits'
          ? 'y-right-ton'
          : 'y-right-count';

      return {
        label: metricLabel,
        // data: avgData.map((item) => item[dataKeyMapping[metricKey]]),
        data: sortedAvgData.map((item) => item[dataKeyMapping[metricKey]]),
        backgroundColor: metric.color,
        yAxisID: axisID,
      };
    }),
  };

  // ----- Tooltip externo con Popper -----
  const externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;

    // si el tooltip está oculto, programa cierre tras la gracia
    if (tooltip.opacity === 0) {
      clearTimeout(graceTimerRef.current);
      if (!tooltipLockedRef.current) {
        graceTimerRef.current = setTimeout(() => forceClose(chart), 150);
      }
      return; // importante: salir aquí
    }

    // si está visible, cancela cualquier cierre pendiente
    clearTimeout(graceTimerRef.current);

    // registra última visibilidad para “gracia”
    const now = (typeof performance !== 'undefined' && performance.now)
      ? performance.now()
      : Date.now();
    if (tooltip.opacity > 0) lastActiveTsRef.current = now;

    // si está oculto y no hay lock ni gracia -> cerrar
    const withinGrace = now - (lastActiveTsRef.current || 0) < 150;
    if (tooltip.opacity === 0 && !tooltipLockedRef.current && !withinGrace) {
      setTooltipState((prev) => ({ ...prev, open: false }));
      return;
    }

    // si no hay puntos, cerrar
    const dps = tooltip.dataPoints || [];
    if (!dps.length) {
      setTooltipState((prev) => ({ ...prev, open: false }));
      return;
    }

    // título del tooltip (si es fecha → formatea)
    const rawTitle = tooltip.title?.[0];
    const title = rawTitle;

    // console.log('rawTitle', rawTitle)

    // filtrar por datasetIndex únicos
    const seen = new Set();
    const uniques = [];
    dps.forEach((dp) => {
      if (!seen.has(dp.datasetIndex)) {
        seen.add(dp.datasetIndex);
        uniques.push(dp);
      }
    });

    // contenido
    const content = (
      <Box>
        {title && (
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: '.5vh', textAlign: 'center', fontSize:'.85vw' }}>
            {title}
          </Typography>
        )}

        <Box sx={{ maxHeight: '20vh', overflowY: 'auto', pr: 1 }}>
          {uniques.map((dp, idx) => {
            const ds = chart.data.datasets[dp.datasetIndex] || {};
            const label = ds.label || '';
            // valor “bonito”
            const raw = dp.parsed?.y ?? dp.formattedValue ?? '';
            const value =
              typeof raw === 'number'
                ? (Number.isInteger(raw) ? raw : Number(raw).toFixed(2))
                : String(raw);

            // color
            let dotColor = ds.backgroundColor || '#aaa';
            if (Array.isArray(dotColor)) dotColor = dotColor[dp.dataIndex] || dotColor[0];

            return (
              <Box
                key={`${dp.datasetIndex}-${dp.dataIndex}-${idx}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'max-content auto',
                  columnGap: 1,
                  alignItems: 'center',
                  mb: '.5vw',
                  fontSize: '.7vw',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '.5vw', minWidth: 0 }}>
                  <span
                    style={{
                      width: '.5vw',
                      height: '.5vw',
                      borderRadius: 2,
                      display: 'inline-block',
                      background: dotColor,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: dotColor, fontWeight: 700, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', fontSize:'.7vw' }}
                    title={label}
                  >
                    {label}:
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ textAlign: 'right', color: '#c3c3c3',fontSize:'.7vw' }}>
                  {value}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );

    // posición (en coordenadas absolutas dentro del viewport)
    const rect = chart.canvas.getBoundingClientRect();
    const x = rect.left + window.pageXOffset + (tooltip.caretX ?? 0);
    const y = rect.top + window.pageYOffset + (tooltip.caretY ?? 0);

    setTooltipState({
      open: true,
      x,
      y,
      content: content,
    });
  };

  useEffect(() => {
    if (!barChartRef.current) return;

    // Configura escalas dinámicamente con ALL_METRICS
    const scaleConfig = metricsToPlot.reduce((scales, metricKey) => {
      const resolvedKey =
        metricKey === 'alarms_by_ton' ? alarmGraph?.[1] : metricKey;
      const metric = ALL_METRICS.find((m) => m.key === resolvedKey) || {
        label: resolvedKey,
        color: '#888',
      };
      const axisID =
        metricKey === 'sfr_value'
          ? 'y-left'
          : metricKey === 'alarms_by_ton' || metricKey === 'alarms_by_visits'
          ? 'y-right-ton'
          : 'y-right-count';

      scales[axisID] = {
        type: 'linear',
        position: axisID === 'y-left' ? 'left' : 'right',
        title: { display: false, text: metric.label },
        ticks: {
          font: { size: fontSizeEjes },
          color: metric.color,
        },
        grid: {
          color: axisID === 'y-left' ? colorLine : 'rgba(255, 255, 255, 0)',
          lineWidth: borderWidth,
        },
      };
      return scales;
    }, {});

    // Instancia el chart
    const chart = new Chart(barChartRef.current, {
      type: 'bar',
      data: barChartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        elements: { rectangle: { minBarLength: 20 } },
        scales: {
          ...scaleConfig,
          x: {
            title: {
              display: true,
              text: titulo,
              font: { size: fontSizeEjes },
              color: '#C3C3C3',
            },
            ticks: {
              font: { size: fontSizeEjes },
              color: '#C3C3C3',
              callback(value) {
                const label = this.getLabelForValue(value);
                if (label?.toUpperCase?.().startsWith('JAULA')) {
                  const [first, ...rest] = String(label).split(' ');
                  return [first, rest.join(' ')];
                }
                return label;
              },
            },
            grid: { color: colorLine, lineWidth: borderWidth },
          },
        },
        plugins: {
          tooltip: {
            enabled: false,
            external: externalTooltipHandler,
            mode: 'index',
            intersect: false,
            displayColors: false,
          },
          legend: {
            position: 'top',
            labels: { boxWidth: window.innerWidth * 0.01, font: { size: fontSizeEjes } },
          },
        },
        indexAxis: 'x',
      },
    });
    barChartInstanceRef.current = chart;
    if (chartInstanceRef) chartInstanceRef.current = chart;
    // rastreo de mouse para mejor “nearest”
    const canvas = barChartRef.current;
    const onMove = (e) => {
      chart._tooltipMouseX = e.clientX;
      chart._tooltipMouseY = e.clientY;
    };
    canvas.addEventListener('mousemove', onMove);

    
    // NUEVO: cierra al salir del canvas (cubre mouse/touch/pointer)
    const onLeave = () => forceClose(chart);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('touchend', onLeave, { passive: true });



    return () => {
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('pointerleave', onLeave);
      canvas.removeEventListener('touchend', onLeave);

      clearTimeout(graceTimerRef.current);
      setTooltipState((prev) => ({ ...prev, open: false }));

      chart.destroy();
      barChartInstanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, alarmGraph, titulo]);

  return (
    <>
      <canvas ref={barChartRef} />

      {/* Tooltip (Material UI) */}
      <Popper
        open={tooltipState.open}
        anchorEl={barChartRef.current}
        style={{
          position: 'absolute',
          left: tooltipState.x,
          top: tooltipState.y,
          pointerEvents: 'auto',
          zIndex: 1300,
        }}
      >
        <Box
          elevation={3}
          sx={{
            p: '0.5vw',
            maxHeight: 260,
            maxWidth: '40vw',
            overflowY: 'auto',
            bgcolor: 'rgba(0,0,0,0.96)',
            color: '#c3c3c3',
            border: '0.01vw solid #e84e0e',
            borderRadius: '.5vw',
          }}
          onMouseEnter={() => {
            tooltipLockedRef.current = true;
          }}
          onMouseLeave={() => {
            tooltipLockedRef.current = false;
            forceClose(barChartInstanceRef.current);
            // programamos un pequeño cierre para evitar parpadeos
            // setTimeout(() => {
            //   setTooltipState((prev) => ({ ...prev, open: false }));
            // }, 120);
          }}
          onWheel={(e) => {
            // evita que el scroll afecte al canvas
            e.stopPropagation();
          }}
        >
          {tooltipState.content}
        </Box>
      </Popper>
    </>
  );
};

export default BarChartSemanal;
